import { api } from '@/api/ApiGateway'
import { Toaster } from '@/models/utils/Toaster.js'
import { useAuthStore } from '@/stores/authStore.js'

/**
 * @TypeDef ResponseObject
 * @property {boolean} success
 * @property {string} error_message
 * @property {string} exception
 * @property {*|*[]} data
 */

class PortalApi
{
	constructor()
	{
		this.toaster = new Toaster();
	}

	/**
	 * Creates an object matching app-service ResponseObject
	 * @param {string|null} p_error_message Error message
	 * @param {[]|null} p_data String
	 * @returns {ResponseObject} Object
	 */
	createErrorResponse(p_error_message, p_data)
	{
		return {
			'success': false,
			'data': p_data ? p_data : [],
			'error_message': p_error_message ? p_error_message : 'An error has occurred.',
			'exception': ''
		}
	}

	/**
	 * Immediately run a report
	 * @param {function} p_on_complete Callback function. Parameters (p_response, p_data, p_error_eh).
	 * @param {string|null} p_err_message Custom error message
	 */
	loadSettings(p_on_complete, p_err_message)
	{
		api.get('portal/settings').then(p_response =>
		{
			try
			{
				this.onResponse(p_response, p_on_complete, p_err_message);
			}
			catch (e)
			{
				this.toaster.error("Error handling load settings response:<br/> " + e.message);
			}
		})
	}

	/**
	 * Immediately run a report
	 * @param {object} p_settings Settings to save
	 * @param {function} p_on_complete Callback function. Parameters (p_response, p_data, p_error_eh).
	 * @param {string|null} p_err_message Custom error message
	 */
	updateSettings(p_settings, p_on_complete, p_err_message)
	{
		api.post(`portal/settings`, { "portal_settings": p_settings } ).then(p_response =>
		{
			try
			{
				this.onResponse(p_response, p_on_complete, p_err_message);
			}
			catch (e)
			{
				this.toaster.error("Error handling update setting response:<br/> " + e.message);
			}
		})
	}

	/**
	 * Handles the response from an API call. If there is an error, onError handles showing error response.
	 * p_on_complete will be called if additional error handling is requried.
	 * @param {Object} p_response Response from request.
	 * @param p_on_complete Callback function
	 * @param {string|null} p_err_message Custom error message
	 */
	onResponse(p_response, p_on_complete, p_err_message)
	{
		if (this.onError(p_response, p_err_message))
		{
			p_on_complete(p_response, this.createErrorResponse(p_err_message, p_response.getData()), true);
		}
		else
		{
			const data = p_response.getData();
			p_on_complete(p_response, data, false);
		}
	}

	/**
	 * Checks if there is an error with the request. If an error is found a banner or toast is shown and true is returned
	 * @param {Object} p_response Response from request.
	 * @param {string|null} p_err_message Custom error message
	 * @returns {boolean} True if an error was found
	 */
	onError(p_response, p_err_message)
	{
		if (p_response.hasError())
		{
			p_response.showErrorBanner()
			return true;
		}

		const data = p_response.getData();
		if (data && data.success)
		{
			return false;
		}
		else if (data && !data.success)
		{
			if (data['exception'] && useAuthStore().isDeveloper)
			{
				this.toaster.error(data.error_message + "<br/><br/>" + data['exception']);
			}
			else
			{
				this.toaster.error(data.error_message)
			}
		}
		else
		{
			p_response.showErrorBanner(p_err_message)
		}
		return true;
	}
}

export const portalApi = new PortalApi()